import React, { useEffect, useState, useMemo } from "react";
import { Icon } from "@iconify/react";
import $ from "jquery";
import "datatables.net-dt/js/dataTables.dataTables";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { NotificationManager } from "react-notifications";
import { fmtDate, fmtFileDate } from "./Copper/copperConfig";
import { useTimezone } from "../hook/useTimezone";

const REPORT_SECTIONS = [
  {
    key: "open-positions",
    title: "Open Positions",
    endpoint: "open-positions",
    responseKey: "open-positions",
    headers: ["Instrument", "Side", "Leverage", "Amount", "Value", "Avg Price", "Mark Price", "Current Market Price", "UPL", "IM", "MM"],
    fields: ["instrument", "side", "leverage", "amount", "value", "avgPrice", "markPrice", "currentPrice", "upl", "im", "mm"],
  },
  {
    key: "todays-transactions",
    title: "Transactions",
    endpoint: "todays-transactions",
    responseKey: "todays-transactions",
    headers: ["Instrument", "Type", "Side", "Amount", "Position", "Price", "Funding", "Fee Rate", "Fee", "Date"],
    fields: ["instrument", "type", "side", "amount", "position", "price", "funding", "feeRate", "fee", "date"],
  },
  {
    key: "balance",
    title: "Account Balance",
    endpoint: "balance",
    responseKey: "balance",
    headers: ["Type", "Currency", "Equity", "Margin Balance", "Initial Margin", "Maintenance Margin", "Available Balance"],
    fields: ["type", "currency", "equity", "marginBalance", "initialMargin", "maintenanceMargin", "availableBalance"],
  },
  {
    key: "settlements",
    title: "Settlement History",
    endpoint: "settlements",
    responseKey: "settlements",
    headers: ["Instrument", "Position Size", "Index Price", "Mark Price", "PNL", "Funding Fee", "Session PNL", "Date"],
    fields: ["instrument", "positionSize", "indexPrice", "markPrice", "pnl", "fundingFee", "sessionPnl", "timestamp"],
  },
  {
    key: "fund-transfers",
    title: "Fund Transfers",
    endpoint: "fund-transfers",
    responseKey: "fund-transfers",
    headers: ["Currency", "Amount", "Direction", "Other Party", "Status", "Date"],
    fields: ["currency", "amount", "direction", "otherParty", "state", "timestamp"],
  },
];

const Clients = () => {
  // Subscribe so any tz-formatted strings re-render when the user picks a
  // different zone (currently nothing on this page renders dates, but the
  // cumulative PDF export reads the live tz when generating).
  useTimezone();
  const navigate = useNavigate();

  const BaseURL = process.env.REACT_APP_API;
  const [Clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    const token = sessionStorage.getItem('AdminAuthToken');
    if (!token) {
      navigate('/');
      return;
    }

    const validateToken = async () => {
      try {
        const response = await fetch(`${BaseURL}/api/auth/validate-token`, {
          method: 'GET',
          headers: { 'Authorization': `Bearer ${token}` },
        });

        const data = await response.json();

        if (!data.success) {
          sessionStorage.removeItem('AdminAuthToken');
          navigate('/');
        }
      } catch (error) {
        console.error('Token validation failed:', error);
        sessionStorage.removeItem('AdminAuthToken');
        navigate('/');
      }
    };

    validateToken();
  }, [navigate]);

  const fetchClients = async () => {
    try {
      const token = sessionStorage.getItem("AdminAuthToken");
      const response = await fetch(`${BaseURL}/api/clients`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error("Failed to fetch Clients");

      const data = await response.json();
      setClients(data.reverse());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  const handleViewPortfolio = (clientId, clientTitle, clientEmail) => {
    const encodedId = btoa(clientId.toString());
    navigate(`/portfolio/${encodedId}`, {
      state: { clientName: clientTitle, clientEmail }
    });
  };

  const handleDownloadReport = async (clientId, clientTitle, clientEmail) => {
    if (!clientId) {
      NotificationManager.error("Invalid client.");
      return;
    }
    const token = sessionStorage.getItem("AdminAuthToken");
    if (!token) {
      navigate("/");
      return;
    }

    setDownloadingId(clientId);
    const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

    try {
      const exchangesRes = await axios.get(
        `${BaseURL}/api/client-apis/${clientId}/exchanges`,
        authHeaders
      );
      const uniqueExchanges = Array.from(
        new Map((exchangesRes.data || []).map((ex) => [ex.exchange_id, ex])).values()
      );

      if (uniqueExchanges.length === 0) {
        NotificationManager.warning("No exchanges found for this client.");
        return;
      }

      const aggregated = [];
      for (const ex of uniqueExchanges) {
        const exchangeId = ex.exchange_id;
        const exchangeName = ex.exchange_name || `Exchange ${exchangeId}`;

        let accounts = [];
        try {
          const accRes = await axios.get(
            `${BaseURL}/api/client-apis/${clientId}/exchange/${exchangeId}/accounts`,
            authHeaders
          );
          accounts = accRes.data || [];
        } catch (e) {
          console.warn(`Accounts fetch failed for exchange ${exchangeId}`, e);
        }

        if (accounts.length === 0) {
          aggregated.push({ exchangeName, accountId: null, sections: [] });
          continue;
        }

        for (const acc of accounts) {
          const accountId = acc.name || acc.account_id || acc.account || acc.id;
          if (!accountId) continue;

          const sections = await Promise.all(
            REPORT_SECTIONS.map(async (sec) => {
              try {
                const res = await axios.get(
                  `${BaseURL}/api/account-tracking/${clientId}/${exchangeId}/${accountId}/${sec.endpoint}`,
                  authHeaders
                );
                const rows = res.data?.[sec.responseKey] || [];
                return { ...sec, rows };
              } catch (e) {
                return { ...sec, rows: [] };
              }
            })
          );

          aggregated.push({ exchangeName, accountId, sections });
        }
      }

      generateCumulativePDF({
        clientId,
        clientTitle,
        clientEmail,
        aggregated,
      });

      NotificationManager.success("Report downloaded.");
    } catch (err) {
      console.error(err);
      NotificationManager.error(
        err?.response?.data?.message || err?.message || "Failed to generate report."
      );
    } finally {
      setDownloadingId(null);
    }
  };

  const generateCumulativePDF = ({ clientId, clientTitle, clientEmail, aggregated }) => {
    const doc = new jsPDF("landscape");
    const pageWidth = doc.internal.pageSize.width;
    const margin = 14;
    const today = fmtDate(Date.now());

    try {
      doc.addImage("/assets/images/BlueAsterAR.png", "PNG", pageWidth - 50, 10, 36, 12);
    } catch (e) { /* logo optional */ }

    doc.setFontSize(18);
    doc.setTextColor(15, 23, 42);
    doc.text("Cumulative Client Report", margin, 18);

    doc.setFontSize(10);
    doc.setTextColor(71, 85, 105);
    doc.text(`Client: ${clientTitle || "—"}`, margin, 26);
    doc.text(`Username: ${clientEmail || "—"}`, margin, 32);
    doc.text(`Generated on: ${today}`, margin, 38);

    let cursorY = 46;

    // Drop sections with no rows; drop groups that end up with nothing to show
    const nonEmpty = aggregated
      .map((g) => ({
        ...g,
        sections: (g.sections || []).filter((s) => Array.isArray(s.rows) && s.rows.length > 0),
      }))
      .filter((g) => g.sections.length > 0);

    if (nonEmpty.length === 0) {
      doc.setFontSize(11);
      doc.setTextColor(100, 100, 100);
      doc.text("No data available for this client.", margin, cursorY);
    }

    nonEmpty.forEach((group) => {
      if (cursorY > doc.internal.pageSize.height - 40) {
        doc.addPage();
        cursorY = 20;
      }

      doc.setFontSize(13);
      doc.setTextColor(29, 78, 216);
      doc.text(`${group.exchangeName} • Account: ${group.accountId}`, margin, cursorY);
      cursorY += 6;

      group.sections.forEach((sec) => {
        doc.setFontSize(11);
        doc.setTextColor(15, 23, 42);
        doc.text(sec.title, margin, cursorY);
        cursorY += 2;

        const body = sec.rows.map((row) =>
          sec.fields.map((f) => {
            let v = row?.[f];
            if (f === "currentPrice" && (v === undefined || v === null || v === "")) {
              v = row?.lastPrice ?? row?.currentMarketPrice ?? row?.indexPrice;
            }
            if (f === "upl" && (v === undefined || v === null || v === "")) {
              v = row?.ups ?? row?.uspl;
            }
            if (v === undefined || v === null) return "N/A";
            // Render epoch/ISO timestamps in the user's selected display tz.
            if (f === "timestamp" || f === "date" || f === "time") {
              return fmtDate(v);
            }
            const str = String(v);
            if (str.trim() === "") return "N/A";
            if (f === "type") return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
            if (f === "leverage") return `${str}x`;
            return str;
          })
        );

        autoTable(doc, {
          head: [sec.headers],
          body,
          startY: cursorY + 2,
          styles: { fontSize: 8 },
          headStyles: { fillColor: [37, 99, 235], textColor: 255 },
          alternateRowStyles: { fillColor: [240, 245, 255] },
          margin: { left: margin, right: margin },
          didParseCell: (data) => {
            if (data.section !== "body") return;
            const fieldName = sec.fields?.[data.column.index];
            const isPnlField = ["upl", "pnl", "sessionPnl"].includes(fieldName);
            const raw = String(data.cell.text?.[0] ?? "").replace("%", "").trim();
            if (raw === "N/A" || raw === "") {
              data.cell.styles.textColor = [148, 163, 184];
              data.cell.styles.fontStyle = "italic";
              return;
            }
            if (!isPnlField) return;
            const n = Number(raw);
            if (Number.isNaN(n)) return;
            if (n > 0) {
              data.cell.styles.textColor = [22, 163, 74];
              data.cell.styles.fontStyle = "bold";
            } else if (n < 0) {
              data.cell.styles.textColor = [220, 38, 38];
              data.cell.styles.fontStyle = "bold";
            }
          },
        });

        cursorY = (doc.lastAutoTable?.finalY || cursorY) + 8;
      });

      cursorY += 4;
    });

    const totalPages = doc.internal.getNumberOfPages();
    doc.setFontSize(8);
    doc.setTextColor(120, 120, 120);
    doc.setFont("helvetica", "italic");
    const disclaimer =
      "Disclaimer: We do not guarantee the accuracy of this report as it is derived from the exchange directly. For any discrepancies pertaining to the transactions, balances, etc please contact the trading exchange.";
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      const ph = doc.internal.pageSize.height;
      const split = doc.splitTextToSize(disclaimer, pageWidth - margin * 2);
      doc.text(split, margin, ph - 10);
      doc.text(`Page ${i} of ${totalPages}`, pageWidth - margin - 25, ph - 4);
    }

    const safeName = (clientTitle || "client").toString().replace(/[^a-z0-9_-]/gi, "_");
    doc.save(`cumulative_report_${safeName}_${fmtFileDate()}.pdf`);
  };

  useEffect(() => {
    if (Clients.length > 0) {
      const table = $("#dataTable").DataTable({ destroy: true });
      return () => {
        table.destroy();
      };
    }
  }, [Clients]);

  const initialsFor = (name = "") => {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return "?";
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  const palette = ["#2563eb", "#0ea5e9", "#7c3aed", "#db2777", "#16a34a", "#f59e0b", "#ef4444", "#0891b2"];
  const colorFor = (key = "") => {
    let hash = 0;
    for (let i = 0; i < key.length; i++) hash = key.charCodeAt(i) + ((hash << 5) - hash);
    return palette[Math.abs(hash) % palette.length];
  };

  const totalClients = useMemo(() => Clients.length, [Clients]);

  if (loading) {
    return (
      <div className="bac-clients-wrap">
        <div className="bac-card bac-card-loading">
          <div className="bac-skel-header" />
          <div className="bac-skel-row" />
          <div className="bac-skel-row" />
          <div className="bac-skel-row" />
          <div className="bac-skel-row" />
        </div>
        {scopedStyles}
      </div>
    );
  }

  if (error) {
    return (
      <div className="bac-clients-wrap">
        <div className="bac-card bac-error-card">
          <Icon icon="solar:danger-triangle-bold-duotone" />
          <div>
            <h6>Something went wrong</h6>
            <p>{error}</p>
          </div>
          <button className="bac-btn bac-btn-primary" onClick={() => { setLoading(true); setError(null); fetchClients(); }}>
            <Icon icon="solar:refresh-bold" /> Retry
          </button>
        </div>
        {scopedStyles}
      </div>
    );
  }

  return (
    <div className="bac-clients-wrap">
      <div className="bac-card">
        {/* Header */}
        <div className="bac-card-head">
          <div className="bac-head-left">
            <span className="bac-head-ico">
              <Icon icon="solar:users-group-rounded-bold-duotone" />
            </span>
            <div>
              <h5>Clients List</h5>
              <p>Manage and view all registered fund tracking clients.</p>
            </div>
          </div>
          <div className="bac-head-right">
            <span className="bac-count-pill">
              <Icon icon="solar:user-id-bold" />
              <strong>{totalClients}</strong>
              <span>Total Clients</span>
            </span>
          </div>
        </div>

        {/* Table */}
        <div className="bac-table-wrap">
          <table className="bac-table" id="dataTable" data-page-length={10}>
            <thead>
              <tr>
                <th style={{ width: 80 }}>Sr No.</th>
                <th>Client Title</th>
                <th>Username</th>
                <th style={{ width: 360, textAlign: "right" }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {Clients.map((member, index) => {
                const title = member.client_title || "-";
                const accent = colorFor(title);
                const isDownloading = downloadingId === member.client_id;
                return (
                  <tr key={member.MemberCode || member.client_id || index}>
                    <td>
                      <span className="bac-srno">{String(index + 1).padStart(2, "0")}</span>
                    </td>
                    <td>
                      <div className="bac-client-cell">
                        <span className="bac-avatar" style={{ background: `${accent}1A`, color: accent }}>
                          {initialsFor(title)}
                        </span>
                        <div className="bac-client-meta">
                          <strong>{title}</strong>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="bac-username">{member.email || "—"}</span>
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <div className="bac-actions">
                        <button
                          className="bac-btn bac-btn-primary"
                          onClick={() => handleViewPortfolio(member.client_id, title, member.email)}
                          title="View Portfolio"
                        >
                          <Icon icon="solar:pie-chart-2-bold-duotone" />
                          View Portfolio
                        </button>
                        <button
                          className="bac-btn bac-btn-ghost"
                          onClick={() => handleDownloadReport(member.client_id, title, member.email)}
                          disabled={isDownloading}
                          title="Download Cumulative Report"
                        >
                          {isDownloading ? (
                            <>
                              <span className="bac-btn-spin" />
                              Generating...
                            </>
                          ) : (
                            <>
                              <Icon icon="solar:document-add-bold-duotone" />
                              Download Report
                            </>
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {Clients.length === 0 && (
                <tr>
                  <td colSpan="4">
                    <div className="bac-empty">
                      <Icon icon="solar:users-group-rounded-bold-duotone" />
                      <h6>No Clients Found</h6>
                      <p>You don't have any clients yet. New clients will show up here.</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      {scopedStyles}
    </div>
  );
};

const scopedStyles = (
  <style>{`
    .bac-clients-wrap { width: 100%; }

    .bac-card {
      background: #fff;
      border-radius: 18px;
      border: 1px solid #eef2f7;
      box-shadow: 0 6px 24px rgba(15, 23, 42, 0.05);
      overflow: hidden;
    }

    /* ===== Header ===== */
    .bac-card-head {
      display: flex; align-items: center; justify-content: space-between;
      gap: 16px; flex-wrap: wrap;
      padding: 22px 24px;
      background:
        radial-gradient(600px 200px at 0% 0%, rgba(37,99,235,0.06), transparent 60%),
        linear-gradient(180deg, #fafbff 0%, #ffffff 100%);
      border-bottom: 1px solid #eef2f7;
    }
    .bac-head-left { display:flex; align-items:center; gap: 14px; min-width: 0; }
    .bac-head-ico {
      width: 46px; height: 46px; flex-shrink: 0;
      display:inline-flex; align-items:center; justify-content:center;
      border-radius: 12px;
      background: linear-gradient(135deg, #2563eb, #0ea5e9);
      color: #fff;
      font-size: 24px;
      box-shadow: 0 8px 18px rgba(37, 99, 235, 0.25);
    }
    .bac-head-left h5 {
      margin: 0; font-size: 18px; font-weight: 700; color: #0f172a; letter-spacing: -0.2px;
    }
    .bac-head-left p {
      margin: 2px 0 0; font-size: 13px; color: #64748b;
    }
    .bac-head-right { display:flex; align-items:center; gap:10px; }
    .bac-count-pill {
      display:inline-flex; align-items:center; gap: 8px;
      padding: 8px 14px;
      background: #eff6ff;
      color: #1d4ed8;
      border-radius: 999px;
      font-size: 13px;
      border: 1px solid #dbeafe;
    }
    .bac-count-pill strong { font-size: 15px; font-weight: 700; }
    .bac-count-pill svg { font-size: 16px; }

    /* ===== Table wrap ===== */
    .bac-table-wrap { padding: 8px 16px 18px; }

    /* DataTables top/bottom controls */
    .bac-table-wrap .dataTables_wrapper .dataTables_length,
    .bac-table-wrap .dataTables_wrapper .dataTables_filter {
      padding: 14px 8px 10px;
      font-size: 13px;
      color: #475569;
    }
    .bac-table-wrap .dataTables_wrapper .dataTables_length select {
      height: 36px; padding: 0 10px;
      border: 1.5px solid #e2e8f0;
      border-radius: 8px;
      background: #fff;
      color: #0f172a;
      margin: 0 6px;
      outline: none;
    }
    .bac-table-wrap .dataTables_wrapper .dataTables_length select:focus { border-color: #2563eb; }
    .bac-table-wrap .dataTables_wrapper .dataTables_filter input {
      height: 38px;
      min-width: 220px;
      padding: 0 14px;
      border: 1.5px solid #e2e8f0;
      border-radius: 10px;
      background: #fff;
      color: #0f172a;
      margin-left: 8px;
      outline: none;
      transition: all .2s ease;
    }
    .bac-table-wrap .dataTables_wrapper .dataTables_filter input:focus {
      border-color: #2563eb;
      box-shadow: 0 0 0 4px rgba(37,99,235,0.12);
    }

    .bac-table-wrap .dataTables_wrapper .dataTables_info {
      padding: 14px 8px;
      font-size: 13px;
      color: #64748b;
    }
    .bac-table-wrap .dataTables_wrapper .dataTables_paginate {
      padding: 12px 8px;
    }
    .bac-table-wrap .dataTables_wrapper .dataTables_paginate .paginate_button {
      padding: 6px 12px !important;
      margin: 0 2px !important;
      border-radius: 8px !important;
      border: 1px solid #e2e8f0 !important;
      background: #fff !important;
      color: #334155 !important;
      font-size: 13px;
    }
    .bac-table-wrap .dataTables_wrapper .dataTables_paginate .paginate_button.current,
    .bac-table-wrap .dataTables_wrapper .dataTables_paginate .paginate_button.current:hover {
      background: linear-gradient(135deg, #2563eb, #1d4ed8) !important;
      border-color: transparent !important;
      color: #fff !important;
      box-shadow: 0 6px 14px rgba(37,99,235,0.25);
    }
    .bac-table-wrap .dataTables_wrapper .dataTables_paginate .paginate_button:hover {
      background: #eff6ff !important;
      border-color: #bfdbfe !important;
      color: #1d4ed8 !important;
    }
    .bac-table-wrap .dataTables_wrapper .dataTables_paginate .paginate_button.disabled,
    .bac-table-wrap .dataTables_wrapper .dataTables_paginate .paginate_button.disabled:hover {
      opacity: .5; background: #fff !important; color: #94a3b8 !important;
    }

    /* ===== Table ===== */
    .bac-table {
      width: 100% !important;
      border-collapse: separate;
      border-spacing: 0;
      font-size: 14px;
    }
    .bac-table thead th {
      background: #f8fafc;
      color: #475569;
      font-size: 12px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.4px;
      padding: 14px 18px;
      text-align: left;
      border-bottom: 1px solid #eef2f7;
      border-top: 1px solid #eef2f7;
      white-space: nowrap;
    }
    .bac-table thead th:first-child { border-top-left-radius: 12px; border-bottom-left-radius: 12px; }
    .bac-table thead th:last-child { border-top-right-radius: 12px; border-bottom-right-radius: 12px; }

    .bac-table tbody td {
      padding: 14px 18px;
      vertical-align: middle;
      color: #1e293b;
      border-bottom: 1px solid #f1f5f9;
      background: #fff;
      transition: background .15s ease;
    }
    .bac-table tbody tr:last-child td { border-bottom: none; }
    .bac-table tbody tr:hover td { background: #f8fbff; }

    .bac-srno {
      display:inline-flex; align-items:center; justify-content:center;
      min-width: 32px; height: 32px;
      padding: 0 8px;
      border-radius: 8px;
      background: #f1f5f9;
      color: #475569;
      font-size: 12.5px;
      font-weight: 600;
    }

    .bac-client-cell { display:flex; align-items:center; gap: 12px; min-width: 0; }
    .bac-avatar {
      width: 40px; height: 40px; flex-shrink: 0;
      display:inline-flex; align-items:center; justify-content:center;
      border-radius: 50%;
      font-weight: 700;
      font-size: 13px;
      letter-spacing: 0.4px;
    }
    .bac-client-meta { display:flex; flex-direction: column; min-width: 0; }
    .bac-client-meta strong {
      font-size: 14px; font-weight: 600; color: #0f172a;
      white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 240px;
    }
    .bac-client-meta small { font-size: 12px; color: #94a3b8; margin-top: 2px; }

    .bac-username {
      display:inline-flex; align-items:center; gap: 8px;
      color: #475569;
      font-size: 13.5px;
      word-break: break-all;
    }
    .bac-username svg { color: #94a3b8; font-size: 16px; flex-shrink: 0; }

    /* ===== Buttons ===== */
    .bac-btn {
      display:inline-flex; align-items:center; gap: 6px;
      padding: 8px 14px;
      border: none;
      border-radius: 10px;
      font-size: 13px;
      font-weight: 600;
      cursor: pointer;
      transition: all .2s ease;
      white-space: nowrap;
    }
    .bac-btn svg { font-size: 16px; }
    .bac-btn-primary {
      color: #fff;
      background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 60%, #0ea5e9 100%);
      background-size: 200% 200%;
      background-position: 0% 50%;
      box-shadow: 0 8px 16px rgba(37, 99, 235, 0.25);
    }
    .bac-btn-primary:hover {
      background-position: 100% 50%;
      transform: translateY(-1px);
      box-shadow: 0 12px 22px rgba(37, 99, 235, 0.32);
    }

    .bac-btn-ghost {
      color: #1d4ed8;
      background: #fff;
      border: 1.5px solid #dbeafe;
    }
    .bac-btn-ghost:hover:not(:disabled) {
      background: #eff6ff;
      border-color: #93c5fd;
      color: #1e3a8a;
    }
    .bac-btn-ghost:disabled {
      opacity: .7;
      cursor: not-allowed;
    }

    .bac-actions {
      display: inline-flex;
      gap: 8px;
      flex-wrap: wrap;
      justify-content: flex-end;
    }

    .bac-btn-spin {
      width: 14px; height: 14px;
      border: 2px solid rgba(37, 99, 235, 0.25);
      border-left-color: #1d4ed8;
      border-radius: 50%;
      animation: bac-spin .8s linear infinite;
      display: inline-block;
    }
    @keyframes bac-spin { to { transform: rotate(360deg); } }

    /* ===== Empty / Error / Loading ===== */
    .bac-empty {
      display:flex; flex-direction:column; align-items:center; justify-content:center;
      padding: 56px 16px;
      color: #64748b;
      gap: 6px;
    }
    .bac-empty svg { font-size: 56px; color: #cbd5e1; }
    .bac-empty h6 { margin: 6px 0 0; font-size: 16px; color: #334155; font-weight: 600; }
    .bac-empty p { margin: 0; font-size: 13px; }

    .bac-error-card {
      display:flex; align-items:center; gap: 14px; padding: 22px 24px; flex-wrap: wrap;
    }
    .bac-error-card > svg { font-size: 36px; color: #ef4444; }
    .bac-error-card h6 { margin: 0; font-size: 15px; color: #0f172a; font-weight: 600; }
    .bac-error-card p { margin: 2px 0 0; font-size: 13px; color: #64748b; }
    .bac-error-card .bac-btn { margin-left: auto; }

    .bac-card-loading { padding: 22px 24px; }
    .bac-skel-header, .bac-skel-row {
      background: linear-gradient(90deg, #f1f5f9 0%, #e2e8f0 50%, #f1f5f9 100%);
      background-size: 200% 100%;
      animation: bac-shimmer 1.4s linear infinite;
      border-radius: 10px;
    }
    .bac-skel-header { height: 28px; width: 240px; margin-bottom: 18px; }
    .bac-skel-row { height: 56px; margin-bottom: 10px; }
    @keyframes bac-shimmer {
      0% { background-position: 200% 0; }
      100% { background-position: -200% 0; }
    }

    /* ===== Responsive ===== */
    @media (max-width: 768px) {
      .bac-card-head { padding: 18px; }
      .bac-head-left h5 { font-size: 16px; }
      .bac-head-left p { font-size: 12px; }
      .bac-table-wrap { padding: 4px 8px 12px; overflow-x: auto; }
      .bac-table thead th, .bac-table tbody td { padding: 12px 12px; }
      .bac-client-meta strong { max-width: 160px; }
      .bac-table-wrap .dataTables_wrapper .dataTables_filter input { min-width: 160px; }
      .bac-actions { flex-direction: column; align-items: stretch; }
      .bac-btn { justify-content: center; }
    }
    @media (max-width: 560px) {
      .bac-card-head { flex-direction: column; align-items: flex-start; }
      .bac-head-right { width: 100%; }
      .bac-table-wrap .dataTables_wrapper .dataTables_length,
      .bac-table-wrap .dataTables_wrapper .dataTables_filter {
        text-align: left !important;
        float: none !important;
        width: 100%;
      }
      .bac-table-wrap .dataTables_wrapper .dataTables_filter input { width: 100%; min-width: 0; margin-left: 0; margin-top: 6px; }
    }
  `}</style>
);

export default Clients;
